import * as React from "react";
import { Quote, Star } from "lucide-react";
import { useInView } from "@/hooks/useInView";

const CLIENTS = [
  {
    id: "wegele",
    name: "Elektrotechnik Wegele",
    branch: "Handwerk",
    logo: "/logos/wegele.png",
    quote:
      "Unsere Website war nach zwei Wochen online und wirkt endlich so professionell wie unsere Arbeit. Seitdem kommen Anfragen rein, ohne dass wir hinterherlaufen müssen.",
    person: "Inhaber",
  },
  {
    id: "fesch",
    name: "FESCH Elektrotechnik",
    branch: "Handwerk",
    logo: "/logos/fesch.png",
    quote:
      "Endlich ein Auftritt, der zu unserem Betrieb passt – klar, schnell und ohne Schnickschnack. Um Technik, Hosting und Updates müssen wir uns null kümmern.",
    person: "Geschäftsleitung",
  },
  {
    id: "mylife",
    name: "My Life",
    branch: "Beautyindustrie",
    logo: "/logos/mylife.png",
    quote:
      "Vision One hat unsere Marke sofort verstanden und genau so umgesetzt, wie wir sie fühlen. Unsere Kundinnen haben die neue Seite direkt kommentiert.",
    person: "Studioleitung",
  },
  {
    id: "elektro-service",
    name: "Elektro & Wärmetechnik",
    branch: "Handwerk",
    logo: "/logos/elektro-service.png",
    quote:
      "Vom ersten Entwurf bis zur fertigen Seite lief alles ruhig und planbar. Das feste Monatspaket macht die Kosten für uns endlich kalkulierbar.",
    person: "Betriebsleitung",
  },
  {
    id: "edith-agay",
    name: "Edith Agay",
    branch: "Design",
    logo: "/logos/edith-agay.png",
    quote:
      "Der erste Entwurf saß bereits – ruhig, hochwertig und genau in meiner Bildsprache. Kommunikation und Umsetzung waren durchgehend angenehm unkompliziert.",
    person: "Inhaberin",
  },
];

const STATS = [
  { value: "60+", label: "umgesetzte Projekte" },
  { value: "6", label: "betreute Branchen" },
  { value: "14", label: "Tage bis zum Entwurf" },
];

const BRANCHES = [
  "Gutachter",
  "Handwerker",
  "Designer",
  "Restaurants",
  "Schulen & Kampfsportakademien",
  "Beautyindustrie",
];

const LogoTile = ({ client }) => (
  <div className="group flex h-24 w-56 shrink-0 items-center justify-center border border-light-border bg-[hsl(0_0%_100%/0.92)] px-7 shadow-[0_18px_40px_-32px_hsl(225_40%_25%/0.6)] transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 hover:border-[hsl(222_70%_60%/0.5)] hover:shadow-[0_24px_50px_-28px_hsl(225_40%_25%/0.7)]">
    <img
      src={client.logo}
      alt={client.name}
      className="max-h-14 w-auto max-w-[150px] object-contain opacity-85 transition-[transform,opacity] duration-500 group-hover:scale-105 group-hover:opacity-100"
      loading="lazy"
    />
  </div>
);

export const SocialProof = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(
      () => setIndex((prev) => (prev + 1) % CLIENTS.length),
      6000
    );
    return () => window.clearInterval(timer);
  }, [paused]);

  const active = CLIENTS[index];

  return (
    <section
      id="kundenstimmen"
      ref={ref}
      className="relative isolate overflow-hidden bg-light text-light-foreground"
      style={{ clipPath: "polygon(0 0, 100% 2.6vw, 100% 100%, 0 100%)" }}
    >
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-light" />
      <div
        aria-hidden
        className="absolute -right-24 top-[-10%] -z-10 h-[420px] w-[560px] rotate-6 rounded-[45%] bg-[radial-gradient(closest-side,hsl(214_80%_72%/0.26),transparent)] blur-2xl"
      />
      <div
        aria-hidden
        className="absolute -left-20 bottom-[-15%] -z-10 h-[420px] w-[520px] rounded-full bg-[radial-gradient(closest-side,hsl(265_70%_74%/0.2),transparent)] blur-2xl"
      />

      <div className="mx-auto max-w-[1500px] px-5 pb-20 pt-28 sm:px-8 sm:pb-24 sm:pt-32 lg:px-12">
        {/* Kopf */}
        <div
          className={`flex flex-col gap-10 transition-[opacity,transform] duration-700 ease-out lg:flex-row lg:items-end lg:justify-between ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="max-w-2xl">
            <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-light-muted">
              Kundenstimmen
            </p>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-light-foreground sm:text-4xl">
              Andere Unternehmen arbeiten
              <br className="hidden sm:block" /> bereits mit Vision&nbsp;One.
            </h2>
          </div>

          <dl className="grid grid-cols-3 gap-4 sm:gap-8 lg:gap-12">
            {STATS.map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <dt className="font-display text-3xl font-light tracking-tight text-light-foreground sm:text-4xl">
                  {stat.value}
                </dt>
                <dd className="mt-1.5 text-[10px] font-light uppercase leading-snug tracking-[0.14em] text-light-muted sm:text-[11px] sm:tracking-[0.16em]">
                  {stat.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Logo-Laufband */}
        <div
          className="relative mt-14 overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage:
              "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
          }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="flex w-max gap-5 motion-safe:animate-marquee"
            style={{ animationPlayState: paused ? "paused" : "running" }}
          >
            {[...CLIENTS, ...CLIENTS].map((client, i) => (
              <LogoTile key={`${client.id}-${i}`} client={client} />
            ))}
          </div>
        </div>

        {/* Kundenstimme */}
        <div
          className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="lg:col-span-8">
            <figure className="relative min-h-[230px] border border-light-border bg-[hsl(0_0%_100%/0.8)] p-8 shadow-[0_30px_70px_-45px_hsl(225_40%_25%/0.6)] sm:p-10">
              <Quote
                className="absolute right-8 top-8 h-8 w-8 text-[hsl(222_70%_60%/0.25)]"
                strokeWidth={1.2}
              />

              <div className="flex items-center gap-1" aria-label="5 von 5 Sternen">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-3.5 w-3.5 fill-[hsl(222_90%_52%)] text-[hsl(222_90%_52%)]"
                  />
                ))}
              </div>

              <blockquote
                key={active.id}
                className="mt-6 max-w-2xl animate-fade-up font-display text-lg font-light leading-relaxed text-light-foreground sm:text-xl"
              >
                „{active.quote}“
              </blockquote>

              <figcaption className="mt-8 flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden border border-light-border bg-[hsl(0_0%_100%)] p-1.5">
                  <img
                    src={active.logo}
                    alt={active.name}
                    className="max-h-full w-auto object-contain"
                  />
                </span>
                <span className="flex flex-col">
                  <span className="font-display text-sm font-light tracking-wide text-light-foreground">
                    {active.name}
                  </span>
                  <span className="mt-0.5 text-xs font-light text-light-muted">
                    {active.person} · {active.branch}
                  </span>
                </span>
              </figcaption>

              {/* Auswahlpunkte */}
              <div className="mt-8 flex items-center gap-2">
                {CLIENTS.map((client, i) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Stimme von ${client.name} anzeigen`}
                    className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                      i === index
                        ? "w-8 bg-[hsl(222_90%_50%)]"
                        : "w-3 bg-light-border hover:bg-[hsl(222_40%_70%)]"
                    }`}
                  />
                ))}
              </div>
            </figure>
          </div>

          {/* Branchen */}
          <div className="lg:col-span-4">
            <p className="font-display text-[11px] font-light uppercase tracking-[0.28em] text-light-muted">
              Betreute Branchen
            </p>
            <ul className="mt-6 flex flex-wrap gap-2.5">
              {BRANCHES.map((branch, i) => (
                <li
                  key={branch}
                  className={`border border-light-border bg-[hsl(0_0%_100%/0.75)] px-4 py-2 text-xs font-light tracking-wide text-light-muted transition-[opacity,transform,color,border-color] duration-500 hover:border-[hsl(222_70%_60%/0.6)] hover:text-light-foreground ${
                    inView ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                  }`}
                  style={{ transitionDelay: `${200 + i * 70}ms` }}
                >
                  {branch}
                </li>
              ))}
            </ul>

            <p className="mt-8 max-w-sm text-sm font-light leading-relaxed text-light-muted">
              Vom Gutachterbüro bis zur Kampfsportakademie: Wir kennen die
              Anforderungen kleiner Teams – und bauen Websites, die genau dort
              funktionieren.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
